import { addDays, addMonths, addYears } from "date-fns";
import {
  isWeekend,
  returnDateFromISOString,
  returnISODateString,
} from "./settlementDateRules";

// LQC-2975 - tenor entry ("5Y", "3M", "1Y6M") as an alternative to picking a date.
// Weekend-only modified following until the holiday check endpoint exists (B14 /
// LQC-2983), so a tenor date on a market holiday is still the user's to correct.

export interface ITenor {
  years: number;
  months: number;
  days: number;
}

const TENOR_PATTERN = /^(\d+[DMY])+$/;
const TENOR_PART_PATTERN = /(\d+)([DMY])/g;

// D, M and Y, alone or combined, any case. Anything else is not a tenor.
export function parseTenor(text: string): ITenor | null {
  const tenor = (text ?? "").trim().toUpperCase();
  if (!TENOR_PATTERN.test(tenor)) return null;
  const parsed: ITenor = { years: 0, months: 0, days: 0 };
  for (const [, amount, unit] of tenor.matchAll(TENOR_PART_PATTERN)) {
    if (unit === "Y") parsed.years += Number(amount);
    if (unit === "M") parsed.months += Number(amount);
    if (unit === "D") parsed.days += Number(amount);
  }
  return parsed;
}

// Forward off a weekend, unless that crosses a month end, then back (LEG_CREATION_PLAN Q5).
export function returnModifiedFollowingDate(date: Date): Date {
  if (!isWeekend(date)) return date;
  let following = date;
  while (isWeekend(following)) following = addDays(following, 1);
  if (following.getMonth() === date.getMonth()) return following;
  let preceding = date;
  while (isWeekend(preceding)) preceding = addDays(preceding, -1);
  return preceding;
}

// "" when either input is unusable, so a caller can leave the date untouched.
// date-fns clamps month ends: 31 Jan + 1M is the last day of February.
export function returnDateFromTenor(tenorText: string, startISO: string): string {
  const tenor = parseTenor(tenorText);
  const start = returnDateFromISOString(startISO);
  if (!tenor || !start) return "";
  const unadjusted = addDays(
    addMonths(addYears(start, tenor.years), tenor.months),
    tenor.days,
  );
  return returnISODateString(returnModifiedFollowingDate(unadjusted));
}
